import { Tier } from '../../models/item-data-models';

export const ORDERED_CURRENCY_TIERS: Tier[] = Object.values(Tier);

// Fixed stack-size buckets for value-aware promotion (snap required size up to the next bucket)
export const CURRENCY_STACK_SIZE_BUCKETS = [3, 5, 10, 20] as const;

export function quantizeStackSize(required: number, buckets: readonly number[] = CURRENCY_STACK_SIZE_BUCKETS): number | null {
  return buckets.find(s => s >= required) ?? null;
}

export function getStackPromotions(params: {
  sourceTier: Tier;
  value: number | undefined;
  absoluteCutoffs: Map<Tier, number>;
  maxSteps: number;
  buckets?: readonly number[];
}): { targetTier: Tier; stackSize: number }[] {
  const {
    sourceTier,
    value,
    absoluteCutoffs,
    maxSteps,
    buckets = CURRENCY_STACK_SIZE_BUCKETS,
  } = params;

  const maxStack = buckets[buckets.length - 1];
  const promotions: { targetTier: Tier; stackSize: number }[] = [];
  const srcIdx = ORDERED_CURRENCY_TIERS.indexOf(sourceTier);
  const hasValue = value != null && value > 0 && !Number.isNaN(value);

  for (let steps = 1; steps <= maxSteps; steps++) {
    const tgtIdx = srcIdx - steps;
    if (tgtIdx < 0) break;
    const targetTier = ORDERED_CURRENCY_TIERS[tgtIdx];
    if (targetTier === sourceTier) break;

    let required: number;
    if (hasValue) {
      const thr = absoluteCutoffs.get(targetTier);
      if (thr == null || thr <= 0) continue;
      required = Math.ceil(thr / value! - 1e-12);
    } else {
      // No economy value: +1 at first bucket, +2 at max bucket
      required = steps === 1 ? buckets[0] : maxStack;
    }

    if (required <= 1 || required > maxStack) continue;

    const quantized = quantizeStackSize(required, buckets);
    if (quantized == null) continue;

    promotions.push({ targetTier, stackSize: quantized });
  }

  return promotions;
}

export function sortStackPromoGroups<T extends { targetTier: Tier; stackSize: number }>(groups: T[]): T[] {
  return [...groups].sort((a, b) => {
    const tierDiff = ORDERED_CURRENCY_TIERS.indexOf(a.targetTier) - ORDERED_CURRENCY_TIERS.indexOf(b.targetTier);
    if (tierDiff !== 0) return tierDiff;
    return b.stackSize - a.stackSize;
  });
}
